import axios from 'axios';
import qs from 'qs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import useSetStore from '../../hooks/useSetStore';
import useSetWizardStep from '../../hooks/useSetWizardStep';
import useStores from '../../hooks/useStores';
import { WizardStep } from '../../types/Wizard';
import Loader from '../Loader/Loader';
import usePromotions from '../../hooks/usePromotions';
import classes from './classes.module.css';
import StoreTile from './components/StoreTile';
import StoreTileNoTimeslot from './components/StoreTileNoTimeslot';

type Inputs = {
  appointment: string;
};

const StoreAndTimeslotSelection: React.FC = () => {
  const [stores, searchStores, setStores] = useStores();
  const [storeUpdated, setStoreUpdated] = useState(false);
  const [sortByEarliestTimeslot, setSortByEarliestTimeslot] = useState(false);
  const setStep = useSetWizardStep();
  const setStore = useSetStore();
  const [loading, setIsLoading] = useState(false);
  const intl = useIntl();
  const [openStore, setOpenStore] = useState<string | null>(null);
  const promotions = usePromotions();
  const handleOpen = useCallback(
    (storeId: string) => () => {
      setOpenStore((prev) => (prev === storeId ? null : storeId));
    },
    [],
  );

  const { storeID } = qs.parse(window.location?.search || '', {
    ignoreQueryPrefix: true,
  });

  const shopNumber =
    storeID && typeof storeID === 'string' && (storeID as string).startsWith('NL')
      ? storeID.substring(2)
      : (storeID as string);

  const shopByNumberUrl = `${process.env.REACT_APP_API_URI}CMSPages/SonovaBookAppointment/GetStoreByShopNumber.ashx`;

  const handleStore = React.useCallback(
    (storeData) => {
      const { city, id, name, street } = storeData.shop;
      setStore({
        city,
        id,
        name,
        shopNumber,
        street,
      }) as any;
      setStep((prev) => (prev + 1) as WizardStep);
    },
    [setStep, setStore, shopNumber],
  );

  //*TODO*: REFACTOR LATER
  React.useEffect(() => {
    const body = { shopNumber };
    const getData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(shopByNumberUrl, body);
        response.data.success ? handleStore(response.data.payload) : null;
      } catch (err) {
        console.error('error loading store', err);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [handleStore, setStep, setStore, shopByNumberUrl, shopNumber])

  React.useEffect(() => {
    const sortByEarliestTimeslot = (window as any).sortByEarliestTimeslot; //set in Kentico page
    if (typeof sortByEarliestTimeslot !== "undefined"){
      setSortByEarliestTimeslot(sortByEarliestTimeslot);
    }
  });

  useEffect(() => {
  if (sortByEarliestTimeslot && stores && storeUpdated) {
    const sortedStores = [...stores].sort((a, b) => {
      if (a.firstAppointmentTimeslot && b.firstAppointmentTimeslot) {
      if (a.firstAppointmentTimeslot.startDateTime
      > b.firstAppointmentTimeslot.startDateTime ) {
        return 1
      } else {
        return -1
      }
      }
      if (a.firstAppointmentTimeslot) {
        return -1
      } else if (b.firstAppointmentTimeslot) {
        return 1
      }
      return -1
    })
    setStoreUpdated(false)
    setStores(sortedStores);
  }
  }, [storeUpdated])

  const heading = useRef<HTMLHeadingElement | null>(null);

  const scrollToHeading = React.useCallback(() => {
    if (heading.current) {
      heading.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [heading]);

  //workaround :D
  React.useEffect(() => {
    scrollToHeading();
  }, [scrollToHeading, stores]);

  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Inputs>();
  const onSubmit: SubmitHandler<Inputs> = (data) => {
    searchStores(data.appointment);
    scrollToHeading();
  };

  if (loading) {
    return <Loader />;
  }



  return (
    <div className={classes.container}>
      <h2>{intl.formatMessage({ id: 'store.title' })}</h2>

      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor='appointment' className={classes.appointmentLabel}>
          {intl.formatMessage({ id: 'store.label' })}
          <button type='submit' className={classes.button} disabled={!isDirty}>
            {intl.formatMessage({ id: 'store.search' })}
          </button>
        </label>
        <input
          {...register('appointment', {
            required: true,
          })}
          id='appointment'
          className={classes.input}
          type='text'
        ></input>
      </form>

      <div className={classes.wrapper}>
        {stores &&
          stores
            // .filter((f) => f.city === data)
            .filter((s) => !promotions || s.promotionDays?.length > 0)
            .map((store) =>
              store.isEbookingEnabled ? (
                <StoreTile
                  key={store.id}
                  store={store}
                  isOpen={openStore === store.id}
                  toggleOpen={handleOpen(store.id)}
                  stores={stores}
                  setStores={setStores}
                  setUpdated={setStoreUpdated}
                />
              ) : (
                <StoreTileNoTimeslot store={store} key={store.id} />
              ),
            )}
      </div>
    </div>
  );
};
StoreAndTimeslotSelection.displayName = 'StoreAndTimeslotSelection';

export default StoreAndTimeslotSelection;
